import { useCallback, useState, useEffect, useRef } from 'react';
import {
  View,
  ActivityIndicator,
  Platform,
  TouchableOpacity,
  TextInput,
  Alert,
  Linking,
  ScrollView,
  Dimensions,
  Image,
} from 'react-native';
import { Text } from '@/components';
import { router, useLocalSearchParams } from 'expo-router';
import TrailMapPreview from '@/components/TrailMapPreview';
import * as Location from 'expo-location';
import ensureForegroundLocationPermission from '@/utils/permissions';
import { MapPin, Calendar, Edit3, Check, X, Navigation, Star, ArrowLeft, ChevronLeft, ChevronRight, Bookmark, Share as ShareIcon, MoreVertical, Trash2 } from 'lucide-react-native';
import * as Haptics from 'expo-haptics';
import { useUpdateTrailName, useUpdateTrailDetails, useSaveTrailBookmark, useRemoveTrailBookmark, useIsTrailSaved, useDeleteTrail } from '@/lib/queries/trails';
import { useSetTrailVisibility } from '@/lib/queries/groups';
import VisibilitySelector from '@/components/VisibilitySelector';
import type { Visibility } from '@/types/group';
import { useAuth } from '@/context/AuthContext';
import { useSelectedDog } from '@/context/SelectedDogContext';
import { useDogs } from '@/context/DogsContext';
import DogPicker from '@/components/DogPicker';
import { useTrailDetail } from '@/lib/queries/trails';
import { Trail } from '@/types/trail';
import { TrailCharts } from '@/components/TrailCharts';
import { formatDistance, formatDuration } from '@/utils/distance';
import colors from '@/constants/colors';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { logEvent } from '@/lib/analytics';
import styles from './[id].styles';
import { Typography } from '@/constants/typography';
import { useTranslation } from 'react-i18next';
import LottieLoader from '@/components/LottieLoader';


const ENVIRONMENT_TAG_OPTIONS = [
  'forest', 'urban', 'beach', 'mountain', 'rural', 'park',
  'shaded', 'sunny', 'crowded', 'quiet', 'paved', 'trail'
];

const DIFFICULTY_OPTIONS = ['easy', 'moderate', 'hard'];

function PartialStar({ size, rating, color, emptyColor }: {
  size: number;
  rating: number; // 0 to 1 fill fraction
  color: string;
  emptyColor: string;
}) {
  // Guard against NaN/Infinity values being passed to native layout
  const safeSize = Number.isFinite(size) ? size : 0;
  const safeFill = Number.isFinite(rating) ? Math.min(1, Math.max(0, rating)) : 0;
  return (
    <View style={{ width: safeSize, height: safeSize }}>
      <Star size={safeSize} color={emptyColor} fill={emptyColor} strokeWidth={0} />
      <View style={{ position: 'absolute', left: 0, top: 0, width: safeSize * safeFill, overflow: 'hidden' }}>
        <Star size={safeSize} color={color} fill={color} strokeWidth={0} />
      </View>
      <View style={{ position: 'absolute', left: 0, top: 0 }}>
        <Star size={safeSize} color={emptyColor} fill="none" strokeWidth={0} />
      </View>
    </View>
  );
}

export default function TrailDetailScreen() {
  const { t } = useTranslation();
  const { id } = useLocalSearchParams();
  const { user, session } = useAuth();
  const token = session?.accessToken;
  const { dogId } = useSelectedDog();
  const { dogProfiles } = useDogs();
  const updateTrailNameMutation = useUpdateTrailName(token);
  const updateTrailDetailsMutation = useUpdateTrailDetails(token);
  const saveTrailBookmarkMutation = useSaveTrailBookmark(token);
  const removeTrailBookmarkMutation = useRemoveTrailBookmark(token);
  const isTrailSaved = useIsTrailSaved(token);
  const setTrailVisibilityMutation = useSetTrailVisibility(token);
  const deleteTrailMutation = useDeleteTrail(token);
  const trailId = typeof id === 'string' ? id : undefined;
  const [isSavingBookmark, setIsSavingBookmark] = useState<boolean>(false);

  // React Query–backed trail detail — automatically caches and refetches
  const trailQuery = useTrailDetail(trailId, session?.accessToken, dogId);
  const trail = trailQuery.data;
  const isLoading = trailQuery.isLoading;
  const isNoAccess = (trailQuery.error as any)?.message === 'NO_ACCESS';
  const [isEditingName, setIsEditingName] = useState(false);
  const [scrolledPastHero, setScrolledPastHero] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [editedRating, setEditedRating] = useState<number>(0);
  const [editedReview, setEditedReview] = useState('');
  const [editedTags, setEditedTags] = useState<string[]>([]);
  const [editedDifficulty, setEditedDifficulty] = useState<string>('');
  const [editedNameInDetails, setEditedNameInDetails] = useState('');
  const [isGettingDirections, setIsGettingDirections] = useState(false);
  const [heroActiveIndex, setHeroActiveIndex] = useState<number>(0);
  const heroScrollRef = useRef<any>(null);
  const heroPageWidth = Dimensions.get('window').width;
  // Web-only pagination chevrons: mouse/trackpad users can't swipe a
  // horizontal ScrollView, and web momentum events never fire. Frosted pill
  // style, vertically centered on the 320px hero.
  const webHeroChevron = (pos: { left?: number; right?: number }) => ({
    position: 'absolute' as const,
    top: 142,
    ...pos,
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: 'rgba(17,24,39,0.55)',
    ...({ backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' } as any),
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    zIndex: 10,
  });
  const insets = useSafeAreaInsets();

  // Internal analytics: one view_trail event per trail per screen mount.
  const viewedTrailRef = useRef<string | null>(null);
  useEffect(() => {
    if (trail && trailId && viewedTrailRef.current !== trailId) {
      viewedTrailRef.current = trailId;
      logEvent('view_trail', { trail_id: trail.id });
    }
  }, [trail, trailId]);
  useEffect(() => {
    if (trail) {
      setEditedName(trail.name || '');
      setEditedRating(trail.rating || 0);
      setEditedReview(trail.review || '');
      setEditedTags(trail.environment_tags || []);
      setEditedDifficulty(trail.difficulty || '');
      setEditedNameInDetails(trail.name || '');
    }
  }, [trail]);

  // Helper to render native icons on mobile and an emoji fallback on web where some icon libs
  // can produce text nodes that raise "Text strings must be rendered within a <Text> component".
  const IconOrEmoji = ({ IconComponent, emoji, size = 18, color }: { IconComponent: any; emoji?: string; size?: number; color?: string }) => {
    if (false) {
      // Use ThemedText so the emoji/text is correctly wrapped in a <Text> component on web
      return <Text style={{ ...Typography.body(color || '#000'), fontSize: size }}>{emoji || '•'}</Text>;
    }
    const Icon = IconComponent;
    return <Icon size={size} color={color} />;
  };

  const handleSaveName = async () => {
    if (!trail || !editedName.trim()) {
      setIsEditingName(false);
      return;
    }

    try {
      await updateTrailNameMutation.mutateAsync({ id: trail.id, name: editedName.trim() });
      // React Query cache is updated by the mutation — trail will refresh automatically
      setIsEditingName(false);
    } catch {
      Alert.alert(t('common.error'), t('trailDetail.failedToUpdateName'));
    }
  };

  const handleSaveDetails = async () => {
    if (!trail) return;

    try {
      if (editedNameInDetails.trim() !== trail.name) {
        await updateTrailNameMutation.mutateAsync({ id: trail.id, name: editedNameInDetails.trim() });
      }
      await updateTrailDetailsMutation.mutateAsync({
        id: trail.id,
        updates: {
          rating: editedRating > 0 ? editedRating : undefined,
        },
      });
      // React Query cache is updated by the mutation — trail will refresh automatically
      setEditedName(editedNameInDetails.trim());
      setIsEditingDetails(false);
      Alert.alert(t('common.success'), t('trailDetail.trailDetailsUpdated'));
    } catch {
      Alert.alert(t('common.error'), t('trailDetail.failedToUpdateDetails'));
    }
  };

  const handleGetDirections = async () => {
    if (true) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
    }

    if (!trail) {
      Alert.alert(t('common.error'), t('trailDetail.locationNotAvailable'));
      return;
    }

    // Determine trail start coordinates: prefer explicit start coords, then normalized coordinates, then raw path
    let destLat: number | undefined = trail.startLatitude as number | undefined;
    let destLng: number | undefined = trail.startLongitude as number | undefined;

    if ((destLat == null || destLng == null) && (trail.coordinates?.length ?? 0) > 0) {
      destLat = trail.coordinates![0].latitude;
      destLng = trail.coordinates![0].longitude;
    }

    if ((destLat == null || destLng == null) && (trail.path?.length ?? 0) > 0) {
      // backend path is [lon, lat]
      destLat = trail.path![0][1];
      destLng = trail.path![0][0];
    }

    if (destLat == null || destLng == null) {
      Alert.alert(t('common.error'), t('trailDetail.trailStartNotAvailable'));
      return;
    }

    setIsGettingDirections(true);
    try {
      const granted = await ensureForegroundLocationPermission();
      if (!granted) {
        Alert.alert(t('trailDetail.permissionRequired'), t('trailDetail.allowLocationDirections'));
        return;
      }

      const userLoc = await (await import('@/utils/location')).getBestAvailableLocation({ accuracy: Location.Accuracy.Highest });
      if (!userLoc) throw new Error('User location unavailable');
      const originLat = userLoc.coords.latitude;
      const originLng = userLoc.coords.longitude;

      // Draw the route to the trail start WITHOUT starting turn-by-turn
      // navigation — users are usually at home and will pick their own mode
      // (drive/walk) in Maps. Both platforms' Maps apps handle this URL.
      const url = `https://www.google.com/maps/dir/?api=1&origin=${originLat},${originLng}&destination=${destLat},${destLng}`;
      logEvent('get_directions', { trail_id: trail.id });
      try {
        await Linking.openURL(url);
      } catch {
        Alert.alert(t('common.error'), t('trailDetail.unableToOpenMaps'));
      }
    } catch (err) {
      console.error('Directions error', err);
      Alert.alert(t('common.error'), t('trailDetail.failedToGetDirections'));
    } finally {
      setIsGettingDirections(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedName(trail?.name || '');
    setIsEditingName(false);
  };

  const handleCancelDetailsEdit = () => {
    setEditedRating(trail?.rating || 0);
    setEditedReview(trail?.review || '');
    setEditedTags(trail?.environment_tags || []);
    setEditedDifficulty(trail?.difficulty || '');
    setEditedNameInDetails(trail?.name || '');
    setIsEditingDetails(false);
  };

  const toggleTag = (tag: string) => {
    if (true) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }
    setEditedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleVisibilityChange = (v: Visibility, g?: string | null) => {
    if (!trail) return;
    setTrailVisibilityMutation.mutate({ trailId: trail.id, visibility: v, sharedGroupId: g ?? null });
  };

  const handleBack = () => {
    if (true) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }
    // Deep-linked entries have no previous screen — default to the explore tab.
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace('/(tabs)/explore');
    }
  };

  const handleEdit = () => {
    if (true) {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    }
    console.log('Edit pressed, id=', id, 'trail?.userId=', trail?.userId, 'user?.id=', user?.id);
    if (typeof id === 'string') {
      router.push(`/trail/${id}/edit`);
    }
  };

  const handleToggleSave = async () => {
    if (!trail) return;
    if (!user) {
      Alert.alert(t('trailDetail.signInRequired'), t('trailDetail.signInToSave'), [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('trailDetail.signIn'), onPress: () => router.push('/login') },
      ]);
      return;
    }

    try {
      if (true) Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
      setIsSavingBookmark(true);
      const saved = isTrailSaved(trail.id);
      if (saved) {
        await removeTrailBookmarkMutation.mutateAsync(trail.id);
      } else {
        await saveTrailBookmarkMutation.mutateAsync(trail.id);
      }
    } catch (err) {
      console.error('Failed to toggle save:', err);
      Alert.alert(t('common.error'), t('trailDetail.failedToUpdateSaved'));
    } finally {
      setIsSavingBookmark(false);
    }
  };

  const handleOpenShareSheet = () => {
    if (true) Haptics.selectionAsync();
    router.push(`/end-walk/share?trailId=${id}` as any);
  };

  const handleDelete = () => {
    if (!trail) return;
    Alert.alert(
      t('common.delete'),
      t('trailDetail.deleteConfirm'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteTrailMutation.mutateAsync(trail.id);
              router.canGoBack() ? router.back() : router.replace('/(tabs)/explore');
            } catch (err) {
              console.error('Failed to delete trail:', err);
              Alert.alert(t('common.error'), t('common.error'));
            }
          },
        },
      ],
    );
  };

  // Design: header "more" menu — Edit/Delete for owners.
  const handleMoreOptions = () => {
    if (!trail) return;
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    const options: any[] = [
      { text: t('common.cancel'), style: 'cancel' as const },
    ];
    if (canEdit) {
      options.unshift(
        { text: t('trailDetail.edit'), onPress: handleEdit },
        { text: t('common.delete'), style: 'destructive' as const, onPress: handleDelete },
      );
    }
    Alert.alert(trail.name || t('trailDetail.trailName'), undefined, options);
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <LottieLoader size={120} />
      </View>
    );
  }

  if (isNoAccess) {
    return (
      <View style={[styles.loadingContainer, { padding: 32 }]}>
        <MapPin size={64} color="#9ca3af" />
        <Text style={styles.loadingText}>{t('trailDetail.noAccess')}</Text>
        <TouchableOpacity style={{ marginTop: 16, paddingHorizontal: 20, paddingVertical: 10, backgroundColor: colors.accentPrimary, borderRadius: 20 }} onPress={() => (router.canGoBack() ? router.back() : router.replace('/(tabs)/explore'))}>
          <Text style={{ color: colors.backgroundPrimary, fontWeight: '600' }}>{t('common.goBack')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  if (!trail) {
    return (
      <View style={styles.loadingContainer}>
        <MapPin size={64} color="#9ca3af" />
        <Text style={styles.loadingText}>{t('trailDetail.trailNotFound')}</Text>
      </View>
    );
  }

  const coords = trail.coordinates ?? [];
  const heroImages = Array.isArray((trail as any).images)
    ? (trail as any).images
      .map((img: any) => (typeof img === 'string' ? img : img?.url))
      .filter((url: any) => typeof url === 'string' && url.length > 0)
    : [];

  const canEdit = trail.userId === user?.id;
  console.log('Can edit: ', canEdit, trail.userId, '-', user?.id)

  const heroSlidesCount = heroImages.length + (heroImages.length > 0 ? 1 : (trail.photo ? 1 : 0));

  // Trigger the header bar a little before the hero (320px) fully scrolls out
  // so the bar is already in place as the images leave.
  const HERO_HEIGHT = 200;

  return (
    <View style={styles.container}>
      <View style={[styles.headerButtons, { top: 0, paddingTop: insets.top + 12 }, scrolledPastHero && styles.headerBarScrolled]}>
        <TouchableOpacity style={styles.headerButton} onPress={handleBack}>
          <ArrowLeft size={24} color={colors.accentPrimary} />
        </TouchableOpacity>
          <View style={[styles.headerRightButtons]}>
            <TouchableOpacity style={styles.headerButton} onPress={handleToggleSave} disabled={isSavingBookmark}>
              {isSavingBookmark ? (
                <ActivityIndicator size="small" color={colors.accentPrimary} />
              ) : (
                <Bookmark size={22} color={trail && isTrailSaved(trail.id) ? colors.accentPrimary : colors.textMuted} />
              )}
            </TouchableOpacity>
            <TouchableOpacity style={styles.headerButton} onPress={handleOpenShareSheet}>
              <ShareIcon size={22} color={colors.accentPrimary} />
            </TouchableOpacity>
            {canEdit && (
              <TouchableOpacity style={styles.headerButton} onPress={handleMoreOptions}>
                <MoreVertical size={22} color={colors.accentPrimary} />
              </TouchableOpacity>
            )}
          </View>
      </View>

      <ScrollView
        style={styles.scrollContainer}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        scrollEventThrottle={16}
        onScroll={(e) => setScrolledPastHero((e.nativeEvent.contentOffset.y || 0) > HERO_HEIGHT)}
      >
        <View style={styles.heroSection}>
          <View style={styles.heroImageContainer}>
            {heroImages.length > 0 ? (
              <>
              <ScrollView
                ref={heroScrollRef}
                horizontal
                pagingEnabled
                style={{ height: 320 }}
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.heroImageScroll}
                nestedScrollEnabled={true}
                onScroll={(e) => {
                  // onScroll (not momentum events) so web wheel/touch drags
                  // update the active page too.
                  const idx = Math.round((e.nativeEvent.contentOffset.x || 0) / heroPageWidth);
                  if (idx !== heroActiveIndex) setHeroActiveIndex(idx);
                }}
                scrollEventThrottle={16}
              >
                {heroImages.map((imgUrl: string, idx: number) => (
                  <TouchableOpacity
                    key={`${imgUrl}-${idx}`}
                    activeOpacity={0.9}
                    // Explicit dimensions: RN-web can't size width:100% children
                    // inside unsized parents (native RN sizes to the child)
                    style={{ width: heroPageWidth, height: 320 }}
                    onPress={() => router.push({ pathname: `/trail/${trail.id}/photos`, params: { index: String(idx) } } as any)}
                  >
                    <Image
                      source={{ uri: imgUrl }}
                      style={{ width: heroPageWidth, height: 320 }}
                      resizeMode="cover"
                    />
                  </TouchableOpacity>
                ))}
                <View style={{ width: heroPageWidth, height: 320 }}>
                  <TrailMapPreview
                    coordinates={coords}
                    path={trail.path}
                    startLatitude={trail.startLatitude}
                    startLongitude={trail.startLongitude}
                    style={{ flex: 1 }}
                    onPress={() => router.push(`/trail/${trail.id}/map`)}
                  />
                </View>
              </ScrollView>
              {Platform.OS === 'web' && heroSlidesCount > 1 && heroActiveIndex > 0 && (
                <TouchableOpacity
                  style={webHeroChevron({ left: 8 })}
                  onPress={() => heroScrollRef.current?.scrollTo({ x: (heroActiveIndex - 1) * heroPageWidth, animated: true })}
                >
                  <ChevronLeft size={16} color="rgba(255,255,255,0.92)" />
                </TouchableOpacity>
              )}
              {Platform.OS === 'web' && heroSlidesCount > 1 && heroActiveIndex < heroSlidesCount - 1 && (
                <TouchableOpacity
                  style={webHeroChevron({ right: 8 })}
                  onPress={() => heroScrollRef.current?.scrollTo({ x: (heroActiveIndex + 1) * heroPageWidth, animated: true })}
                >
                  <ChevronRight size={16} color="rgba(255,255,255,0.92)" />
                </TouchableOpacity>
              )}
              </>
            ) : trail.photo ? (
              <TouchableOpacity
                activeOpacity={0.9}
                onPress={() => router.push({ pathname: `/trail/${trail.id}/photos`, params: { index: '0' } } as any)}
              >
                <Image
                  source={{ uri: trail.photo }}
                  style={styles.heroImage}
                  resizeMode="cover"
                />
              </TouchableOpacity>
            ) : (
              <TrailMapPreview
                coordinates={coords}
                path={trail.path}
                startLatitude={trail.startLatitude}
                startLongitude={trail.startLongitude}
                style={styles.heroImage}
                onPress={() => router.push(`/trail/${trail.id}/map`)}
              />
            )}

          </View>
        </View>

        <View style={styles.contentWrapper}>
          {/* Design: dots sit at the top of the content card (no counter) */}
          {heroSlidesCount > 1 && (
            <View style={styles.paginationDots} pointerEvents="none">
              {new Array(heroSlidesCount).fill(0).map((_, i) => {
                const active = heroActiveIndex === i;
                return <View key={`hero-dot-${i}`} style={[styles.dot, active && styles.dotActive]} />;
              })}
            </View>
          )}
          <View style={styles.content}>
              <View style={styles.titleSection}>
                <View style={styles.dateRow}>
                  <IconOrEmoji IconComponent={Calendar} emoji="📅" size={16} color={colors.textMuted} />
                  <Text style={styles.dateText}>{new Date(trail.createdAt ?? trail.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Text>
                </View>
                {canEdit && isEditingName ? (
                  <View style={styles.nameEditContainer}>
                    <TextInput
                      style={styles.nameInput}
                      value={editedName}
                      onChangeText={setEditedName}
                      placeholder={t('trailDetail.enterTrailName')}
                      autoFocus
                      placeholderTextColor="#9ca3af"
                    />
                    <View style={styles.editButtons}>
                      <TouchableOpacity
                        style={[styles.iconButton, styles.saveIconButton]}
                        onPress={handleSaveName}
                      >
                        <Check size={18} color="#fff" />
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.iconButton, styles.cancelIconButton]}
                        onPress={handleCancelEdit}
                      >
                        <X size={18} color={colors.textPrimary} />
                      </TouchableOpacity>
                    </View>
                  </View>
                ) : (
                  <View style={styles.titleRow}>
                    <Text style={styles.title}>{trail.name || `Trail ${new Date(trail.createdAt ?? trail.date).toLocaleDateString()}`}</Text>
                  </View>
                )}

                <View style={styles.authorRow}>
                  {trail.createdByUserName ? (
                        <View style={styles.avatar}>
                          <Image
                            source={trail.createdByUserImage ? { uri: trail.createdByUserImage } : undefined}
                            style={{ width: '100%', height: '100%', borderRadius: 18 }}
                            resizeMode="cover"
                          />
                        </View>
                  ) : (
                    <View style={styles.avatar} />
                  )}
                  <Text style={styles.authorName}>{trail.createdByUserName || t('trailDetail.unknownAuthor')}</Text>
                </View>

                {/* Audience badge + dog picker on one row */}
                <View style={{ flexDirection: 'row', alignItems: 'center', marginVertical: 8 }}>
                  {canEdit ? (
                    <VisibilitySelector
                      token={token}
                      visibility={(trail.visibility ?? (trail.isPublic ? 'public' : 'public')) as Visibility}
                      sharedGroupId={trail.sharedGroupId ?? null}
                      onChange={handleVisibilityChange}
                    />
                  ) : trail.visibility && trail.visibility !== 'public' ? (
                    <Text style={{ ...Typography.caption(colors.textMuted), fontSize: 12 }}>{t(`visibility.${trail.visibility}`)}</Text>
                  ) : null}

                  <View style={{ flex: 1 }}>
                    <DogPicker compact />
                  </View>
                </View>
              </View>

              <View style={styles.statsSection}>
                <View style={styles.statCard}>
                  <Text style={styles.statLabel}>{t('trailDetail.estimatedTime')}</Text>
                  <Text style={styles.statValue}>{formatDuration(trail.duration)}</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={styles.statLabel}>{t('trailDetail.length')}</Text>
                  <Text style={styles.statValue}>{formatDistance(trail.distance)}</Text>
                </View>

                <View style={styles.statCard}>
                  <Text style={styles.statLabel}>{t('walkSummary.elevation')}</Text>
                  <Text style={styles.statValue}>{trail.maxElevation ? `${Math.round(trail.maxElevation)}m` : '0m'}</Text>
                </View>
                {dogProfiles.length > 0 ? (
                  <TouchableOpacity style={styles.statCard} onPress={() => router.push(`/trail/${trail.id}/match`) }>
                    <Text style={styles.statLabel}>{t('trailDetail.match')}</Text>
                    <Text style={styles.statValue}>{trail.dogMatchScore ? `${Math.round(trail.dogMatchScore)}%` : 'N/A'}</Text>
                  </TouchableOpacity>
                ) : (
                  <View style={styles.statCard}>
                    <Text style={styles.statLabel}>{t('trailDetail.match')}</Text>
                    <Text style={[styles.statValue, { opacity: 0.4 }]}>{t('matchDetail.noDog')}</Text>
                  </View>
                )}
              </View>

              {(trail.description || trail.review) && !isEditingDetails && (
                <View style={styles.reviewCard}>
                  <Text style={styles.reviewText}>{trail.description || trail.review}</Text>
                </View>
              )}

              {canEdit && isEditingDetails && (
                <View style={styles.editSection}>
                  <Text style={styles.editSectionTitle}>{t('trailDetail.trailName')}</Text>
                  <TextInput
                    style={styles.nameInputInDetails}
                    value={editedNameInDetails}
                    onChangeText={setEditedNameInDetails}
                    placeholder={t('trailDetail.enterTrailName')}
                    placeholderTextColor="#9ca3af"
                  />

                  <Text style={[styles.editSectionTitle, { marginTop: 24 }]}>{t('trailDetail.rating')}</Text>
                  <View style={styles.ratingStars}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <TouchableOpacity
                        key={star}
                        onPress={() => {
                          if (true) {
                            Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                          }
                          setEditedRating(star);
                        }}
                        style={styles.starButton}
                      >
                        <Star
                          size={32}
                          color={star <= editedRating ? '#f59e0b' : '#d1d5db'}
                          fill={star <= editedRating ? '#f59e0b' : 'none'}
                        />
                      </TouchableOpacity>
                    ))}
                  </View>

                  <Text style={[styles.editSectionTitle, { marginTop: 24 }]}>{t('trailDetail.review')}</Text>
                  <TextInput
                    style={styles.reviewInput}
                    value={editedReview}
                    onChangeText={setEditedReview}
                    placeholder={t('trailReviews.reviewPlaceholder')}
                    placeholderTextColor="#9ca3af"
                    multiline
                    numberOfLines={4}
                    textAlignVertical="top"
                  />

                  <Text style={[styles.editSectionTitle, { marginTop: 24 }]}>{t('trailDetail.difficulty')}</Text>
                  <View style={styles.difficultyOptions}>
                    {DIFFICULTY_OPTIONS.map((difficulty) => (
                      <TouchableOpacity
                        key={difficulty}
                        style={[
                          styles.difficultyChip,
                          editedDifficulty === difficulty && styles.difficultyChipSelected,
                        ]}
                        onPress={() => {
                          if (true) {
                            Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                          }
                          setEditedDifficulty(difficulty);
                        }}
                      >
                        <Text
                          style={[
                            styles.difficultyChipText,
                            editedDifficulty === difficulty && styles.difficultyChipTextSelected,
                          ]}
                        >
                          {t(`filters.${difficulty}`)}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>

                  <Text style={[styles.editSectionTitle, { marginTop: 24 }]}>{t('trailEdit.environment')}</Text>
                  <View style={styles.tagsGrid}>
                    {ENVIRONMENT_TAG_OPTIONS.map((tag) => (
                      <TouchableOpacity
                        key={tag}
                        style={[
                          styles.editTagChip,
                          editedTags.includes(tag) && styles.editTagChipSelected,
                        ]}
                        onPress={() => toggleTag(tag)}
                      >
                        <Text
                          style={[
                            styles.editTagChipText,
                            editedTags.includes(tag) && styles.editTagChipTextSelected,
                          ]}
                        >
                          {t(`explore.${tag}`, tag)}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>

                  <View style={styles.editActionButtons}>
                    <TouchableOpacity
                      style={[styles.actionButton, styles.cancelActionButton]}
                      onPress={handleCancelDetailsEdit}
                    >
                      <X size={20} color={colors.textPrimary} />
                      <Text style={styles.cancelActionText}>{t('common.cancel')}</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.actionButton, styles.saveActionButton]}
                      onPress={handleSaveDetails}
                    >
                      <Check size={20} color="#fff" />
                      <Text style={styles.saveActionText}>{t('common.save')}</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              )}

              <TouchableOpacity
                onPress={() => {
                  if (true) {
                    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
                  }
                  router.push(`/trail/${trail.id}/reviews`);
                }}
              >
                <Text style={styles.reviewsTitle}>{`REVIEWS${Number.isFinite(trail.reviewCount) && trail.reviewCount! > 0 ? ` (${trail.reviewCount})` : ''}`}</Text>
                <View style={styles.reviewsRow}>
                  <View style={styles.reviewStars}>
                    {[1, 2, 3, 4, 5].map((s) => {
                      const r = trail.rating || 0;
                      const fill = Math.min(1, Math.max(0, r - (s - 1)));
                      return (
                        <PartialStar
                          key={s}
                          size={24}
                          rating={fill}
                          color={colors.accentPrimary}
                          emptyColor={colors.backgroundSecondaryVarient}
                        />
                      );
                    })}
                  </View>
                  <IconOrEmoji IconComponent={ChevronRight} emoji=">" size={24} color={colors.textMuted} />
                </View>
              </TouchableOpacity>

              {trail.trailPoints && trail.trailPoints.length > 0 && (
                <TrailCharts
                  points={trail.trailPoints}
                  sniffSeconds={trail.sniffTime}
                  durationSeconds={trail.duration}
                />
              )}
            </View>
        </View>
      </ScrollView>

      {/* Sticky bottom footer: directions + Start Trail always visible */}
      <View style={[styles.bottomFooter, { paddingBottom: Math.max(16, insets.bottom + 12) }]}>
        <View style={styles.buttonsRow}>
          <TouchableOpacity
            style={[styles.directionsButton, isGettingDirections && { opacity: 0.7 }]}
            onPress={handleGetDirections}
            disabled={isGettingDirections}
          >
            {isGettingDirections ? (
              <ActivityIndicator size="small" color={colors.accentPrimary} />
            ) : (
              <Navigation size={22} color={colors.accentPrimary} />
            )}
          </TouchableOpacity>

          {Platform.OS !== 'web' && (
            <TouchableOpacity style={styles.startButtonInline} onPress={() => {
              if (true) {
                Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
              }
              logEvent('start_trail', { trail_id: trail.id });
              router.push(`/(tabs)/follow?trailId=${trail.id}`);
            }}>
              <Text style={styles.startButtonText}>{t('record.startTrail')}</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>

    </View>
  );
}
